// src/pages/legalentity/LegalEntityDetailPage.tsx
// Legal entity detail / edit — requisites, verification panel, signatory,
// banking, lifecycle status, default flag, archive.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PaymentMethodsPanel } from '@/features/legalentity/PaymentMethodsPanel';
import { errMessage, LEGAL_ENTITY_STATUS_KEYS, LEGAL_ENTITY_STATUS_VARIANT } from '@/features/legalentity/lib';
import { GetLegalEntity } from '@core/api/endpoints/legalentity/get_legal_entity';
import { UpdateLegalEntity } from '@core/api/endpoints/legalentity/update_legal_entity';
import { DeleteLegalEntity } from '@core/api/endpoints/legalentity/delete_legal_entity';
import { VerifyLegalEntity } from '@core/api/endpoints/legalentity/verify_legal_entity';
import { ChangeLegalEntityStatus } from '@core/api/endpoints/legalentity/change_legal_entity_status';
import { SetDefaultLegalEntity } from '@core/api/endpoints/legalentity/set_default_legal_entity';
import { UpdateDefaultSignatory } from '@core/api/endpoints/legalentity/update_default_signatory';
import type { LegalEntity, LegalEntityStatus } from '@core/types/legalentity';

const STATUSES: LegalEntityStatus[] = [
  'LEGAL_ENTITY_STATUS_DRAFT', 'LEGAL_ENTITY_STATUS_ACTIVE', 'LEGAL_ENTITY_STATUS_SUSPENDED',
  'LEGAL_ENTITY_STATUS_LIQUIDATED', 'LEGAL_ENTITY_STATUS_ARCHIVED',
];

export const LegalEntityDetailPage = observer(() => {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [entity, setEntity] = useState<LegalEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Editable requisites
  const [shortName, setShortName] = useState('');
  const [fullName, setFullName] = useState('');
  const [primaryTaxId, setPrimaryTaxId] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [savingRequisites, setSavingRequisites] = useState(false);

  // Verification
  const [verifySource, setVerifySource] = useState('manual');
  const [verifyReference, setVerifyReference] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Signatory
  const [sigPosition, setSigPosition] = useState('');
  const [sigName, setSigName] = useState('');
  const [sigBasis, setSigBasis] = useState('');
  const [savingSignatory, setSavingSignatory] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [busyDefault, setBusyDefault] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await GetLegalEntity(id);
      const le = res.legal_entity ?? null;
      setEntity(le);
      if (le) {
        setShortName(le.short_name ?? '');
        setFullName(le.full_name ?? '');
        setPrimaryTaxId(le.primary_tax_id ?? '');
        setContactEmail(le.contact_email ?? '');
        setContactPhone(le.contact_phone ?? '');
        setSigPosition(le.default_signatory?.position ?? '');
        setSigName(le.default_signatory?.full_name ?? '');
        setSigBasis(le.default_signatory?.authority_basis ?? '');
      }
    } catch (e) {
      setError(errMessage(e, t('legalentity.detail.loadError')));
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const statusOptions: SelectOption[] = STATUSES.map((s) => ({ value: s, label: t(`legalentity.status.${LEGAL_ENTITY_STATUS_KEYS[s]}`) }));

  const saveRequisites = async () => {
    if (!entity?.entity) return;
    setSavingRequisites(true);
    setError(null);
    setNotice(null);
    try {
      const res = await UpdateLegalEntity(entity.entity.id, {
        legal_entity: { ...entity, short_name: shortName, full_name: fullName, primary_tax_id: primaryTaxId, contact_email: contactEmail, contact_phone: contactPhone },
        update_mask: ['short_name', 'full_name', 'primary_tax_id', 'contact_email', 'contact_phone'],
      });
      setEntity(res.legal_entity ?? entity);
      setNotice(t('legalentity.detail.saved'));
    } catch (e) {
      setError(errMessage(e, t('legalentity.detail.saveError')));
    } finally {
      setSavingRequisites(false);
    }
  };

  const doVerify = async () => {
    if (!id) return;
    setVerifying(true);
    setError(null);
    setNotice(null);
    try {
      const res = await VerifyLegalEntity(id, {
        legal_entity_id: id,
        verification_source: verifySource,
        verification_reference: verifyReference,
        evidence: {},
      });
      setEntity(res.legal_entity ?? entity);
      setNotice(t('legalentity.verification.verified'));
    } catch (e) {
      setError(errMessage(e, t('legalentity.verification.error')));
    } finally {
      setVerifying(false);
    }
  };

  const changeStatus = async (newStatus: string) => {
    if (!id) return;
    setBusyStatus(true);
    setError(null);
    try {
      const res = await ChangeLegalEntityStatus(id, { legal_entity_id: id, new_status: newStatus as LegalEntityStatus, reason: '' });
      setEntity(res.legal_entity ?? entity);
    } catch (e) {
      setError(errMessage(e, t('legalentity.detail.statusError')));
    } finally {
      setBusyStatus(false);
    }
  };

  const setAsDefault = async () => {
    if (!id) return;
    setBusyDefault(true);
    setError(null);
    try {
      const res = await SetDefaultLegalEntity(id, { legal_entity_id: id });
      setEntity(res.legal_entity ?? entity);
    } catch (e) {
      setError(errMessage(e, t('legalentity.detail.defaultError')));
    } finally {
      setBusyDefault(false);
    }
  };

  const saveSignatory = async () => {
    if (!id) return;
    setSavingSignatory(true);
    setError(null);
    setNotice(null);
    try {
      const res = await UpdateDefaultSignatory(id, {
        legal_entity_id: id,
        signatory: {
          position: sigPosition,
          full_name: sigName,
          display_name_in_preamble: sigName,
          authority_basis: sigBasis,
          authority_basis_locale: locale,
          contact_email: '',
          contact_phone: '',
        },
      });
      setEntity(res.legal_entity ?? entity);
      setNotice(t('legalentity.signatory.saved'));
    } catch (e) {
      setError(errMessage(e, t('legalentity.signatory.error')));
    } finally {
      setSavingSignatory(false);
    }
  };

  const doDelete = async () => {
    if (!id) return;
    try {
      await DeleteLegalEntity(id);
      navigate('/legalentity');
    } catch (e) {
      setError(errMessage(e, t('legalentity.detail.deleteError')));
      setDeleteOpen(false);
    }
  };

  if (isLoading && !entity) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-6" aria-busy="true">
        <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (error && !entity) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Alert variant="error">{error}</Alert>
        <Button size="sm" variant="secondary" className="mt-3" onClick={load}>
          {t('legalentity.retry')}
        </Button>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="text-sm text-muted-foreground">{t('legalentity.detail.notFound')}</p>
      </div>
    );
  }

  const status = entity.status ?? 'LEGAL_ENTITY_STATUS_DRAFT';

  const requisitesDirty =
    shortName !== (entity.short_name ?? '') ||
    fullName !== (entity.full_name ?? '') ||
    primaryTaxId !== (entity.primary_tax_id ?? '') ||
    contactEmail !== (entity.contact_email ?? '') ||
    contactPhone !== (entity.contact_phone ?? '');

  const signatoryDirty =
    sigPosition !== (entity.default_signatory?.position ?? '') ||
    sigName !== (entity.default_signatory?.full_name ?? '') ||
    sigBasis !== (entity.default_signatory?.authority_basis ?? '');

  const fieldClass = 'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-6">
      <Link to="/legalentity" className="text-sm text-primary hover:underline">
        {t('legalentity.detail.backToList')}
      </Link>

      {error && <Alert variant="error">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{entity.short_name}</h1>
            <Badge variant={LEGAL_ENTITY_STATUS_VARIANT[status] ?? 'default'} size="sm">
              {t(`legalentity.status.${LEGAL_ENTITY_STATUS_KEYS[status]}`)}
            </Badge>
            {entity.is_default && <Badge variant="primary" size="sm">{t('legalentity.detail.default')}</Badge>}
            {entity.is_verified && <Badge variant="success" size="sm">{t('legalentity.detail.verified')}</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{entity.full_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {!entity.is_default && (
            <Button size="sm" variant="secondary" isLoading={busyDefault} onClick={setAsDefault}>
              {t('legalentity.detail.setDefault')}
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => setDeleteOpen(true)}>
            {t('legalentity.detail.archive')}
          </Button>
        </div>
      </div>

      <section className="space-y-1 rounded-lg border border-border p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('legalentity.detail.requisites')}</h2>
        <FormRow label={t('legalentity.field.shortName')} htmlFor="le-edit-short-name">
          <input id="le-edit-short-name" value={shortName} onChange={(e) => setShortName(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.field.fullName')} htmlFor="le-edit-full-name">
          <input id="le-edit-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.field.primaryTaxId')} htmlFor="le-edit-tax-id">
          <input id="le-edit-tax-id" value={primaryTaxId} onChange={(e) => setPrimaryTaxId(e.target.value)} className={`${fieldClass} font-mono`} />
        </FormRow>
        <FormRow label={t('legalentity.field.contactEmail')} htmlFor="le-edit-email">
          <input id="le-edit-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.field.contactPhone')} htmlFor="le-edit-phone">
          <input id="le-edit-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.detail.lifecycleStatus')}>
          <Select value={status} onChange={(e) => changeStatus(e.target.value)} options={statusOptions} disabled={busyStatus} fullWidth={false} className="w-48" />
        </FormRow>
        <div className="pt-2">
          <Button size="sm" onClick={saveRequisites} isLoading={savingRequisites} disabled={!requisitesDirty}>
            {t('common.saveChanges')}
          </Button>
        </div>
      </section>

      <section className="space-y-1 rounded-lg border border-border p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('legalentity.verification.title')}</h2>
        {entity.is_verified ? (
          <p className="text-sm text-muted-foreground">{t('legalentity.verification.alreadyVerified')}</p>
        ) : (
          <>
            <FormRow label={t('legalentity.verification.source')} htmlFor="le-verify-source">
              <input id="le-verify-source" value={verifySource} onChange={(e) => setVerifySource(e.target.value)} className={fieldClass} />
            </FormRow>
            <FormRow label={t('legalentity.verification.reference')} htmlFor="le-verify-reference">
              <input id="le-verify-reference" value={verifyReference} onChange={(e) => setVerifyReference(e.target.value)} className={fieldClass} />
            </FormRow>
            <div className="pt-2">
              <Button size="sm" onClick={doVerify} isLoading={verifying}>
                {t('legalentity.verification.cta')}
              </Button>
            </div>
          </>
        )}
      </section>

      <section className="space-y-1 rounded-lg border border-border p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('legalentity.signatory.title')}</h2>
        <FormRow label={t('legalentity.signatory.field.position')} htmlFor="le-sig-position">
          <input id="le-sig-position" value={sigPosition} onChange={(e) => setSigPosition(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.signatory.field.fullName')} htmlFor="le-sig-name">
          <input id="le-sig-name" value={sigName} onChange={(e) => setSigName(e.target.value)} className={fieldClass} />
        </FormRow>
        <FormRow label={t('legalentity.signatory.field.authorityBasis')} htmlFor="le-sig-basis">
          <textarea
            id="le-sig-basis"
            value={sigBasis}
            onChange={(e) => setSigBasis(e.target.value)}
            rows={2}
            className={`${fieldClass} resize-none`}
          />
        </FormRow>
        <div className="pt-2">
          <Button size="sm" onClick={saveSignatory} isLoading={savingSignatory} disabled={!signatoryDirty}>
            {t('common.saveChanges')}
          </Button>
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold text-foreground">{t('legalentity.paymentMethods.sectionTitle')}</h2>
        <PaymentMethodsPanel legalEntityId={id ?? ''} />
      </section>

      <ConfirmDialog
        open={deleteOpen}
        title={t('legalentity.detail.archiveConfirmTitle')}
        description={t('legalentity.detail.archiveConfirmDescription')}
        destructive
        onCancel={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
});
